import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase/firebaseConfig";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "./firebase/firebaseConfig";
import { FaTools } from "react-icons/fa";


import MaintenancePage from "./pages/MaintenancePage";

// Context Providers
import { ThemeProvider } from "./context/ThemeContext";
import { CollegeProvider } from "./context/CollegeContext";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";
import { AdminProvider, useAdmin } from "./admin-page/admin-context/AdminContext";

// Components
import LoadingSpinner from "./components/LoadingSpinner";
import Navbar from "./Navbar";

// Pages
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import CustomizePDF from "./pages/CustomizePDF";
import Cart from "./pages/Cart";
import OrderHistory from "./pages/OrderHistory";
import LandingPage from "./pages/LandingPage";
import TermsAndConditions from "./pages/Term_condition";
import PrivacyPolicy from "./pages/privacy_policy";
import Contact_us from "./pages/Contact_us";
import RefundPolicy from "./pages/Refund_policy";
import AdminLogin from "./admin-page/Admin-login";
import AdminDashboard from "./admin-page/Admin-dashboard";
import Transactions from "./pages/TransitionsPage";
import ProtectedRoute from "./ProtectedRoute";
import ShippingPolicy from "./pages/Shipping_policy";
import VerifyEmail from "./pages/VerifyEmail";
import DevelopersPage from "./pages/developers_page";
import PricingPage from "./pages/PricingPage";




function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    let unsubscribeMaintenance;
    let unsubscribeAdminCheck;

    const checkAdminStatus = async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setIsAdmin(userDoc.data().isAdmin || false);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
        } finally {
          setAdminLoading(false);
        }
      } else {
        setIsAdmin(false);
        setAdminLoading(false);
      }
    };

    const unsubscribeAuthAdmin = onAuthStateChanged(auth, (user) => {
      checkAdminStatus(user);
    });

    const checkMaintenanceMode = () => {
      try {
        const docRef = doc(db, "settings", "maintenanceMode");
        unsubscribeMaintenance = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            setIsMaintenance(docSnap.data().enabled);
          }
          setLoadingMaintenance(false);
        });
      } catch (error) {
        console.error("Error checking maintenance mode:", error);
        setLoadingMaintenance(false);
      }
    };

    checkMaintenanceMode();

    return () => {
      if (unsubscribeMaintenance) unsubscribeMaintenance();
      if (unsubscribeAuthAdmin) unsubscribeAuthAdmin();
    };
  }, []);

  if (loadingMaintenance || adminLoading) {
    return <LoadingSpinner />;
  }

  // Check if the current route is an admin route
  const isAdminRoute = window.location.pathname.startsWith("/admin-dashboard");

  // Show maintenance page only if:
  // 1. Maintenance mode is ON
  // 2. The current route is NOT an admin route
  // 3. The user is NOT an admin
  if (isMaintenance && !isAdminRoute && !isAdmin) {
    return (
      <ThemeProvider>
        <ToastContainer autoClose={2500} position="top-right" />
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <AdminProvider>
      <ThemeProvider>
        <AuthProvider>
          <CollegeProvider>
            <CartProvider>
              <Router>
                <ConditionalNavbar />
                <ToastContainer autoClose={2500} position="top-right" />
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/admin-dashboard-login" element={<AdminLogin />} />
                  <Route path="/email-Verification" element={<VerifyEmail />} />

                  {/* Legal Pages */}
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/contact-us" element={<Contact_us />} />
                  <Route path="/shippingPolicy" element={<ShippingPolicy />} />
                  <Route path="/developer_page" element={<DevelopersPage />} />
                  <Route path="/pricing" element={<PricingPage />} />

                  {/* Admin Route - Always accessible regardless of maintenance mode */}
                  <Route path="/admin-dashboard" element={<AdminRoute />} />

                  {/* Protected Routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/customize" element={<CustomizePDF />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/orders" element={<OrderHistory />} />
                    <Route path="/transactions" element={<Transactions />} />
                  </Route>
                </Routes>
              </Router>
            </CartProvider>
          </CollegeProvider>
        </AuthProvider>
      </ThemeProvider>
    </AdminProvider>
  );
}

// Hide Navbar on Admin pages
function ConditionalNavbar() {
  const location = useLocation();
  const hideNavbarRoutes = ["/admin-dashboard", "/admin-dashboard-login", "/auth", "/"];

  return !hideNavbarRoutes.includes(location.pathname) ? <Navbar /> : null;
}

// Custom Route to handle admin condition
function AdminRoute() {
  const { isAdmin, isLoading } = useAdmin();
  if (isLoading) {
    return <LoadingSpinner />;
  }
  return isAdmin ? <AdminDashboard /> : <Navigate to="/admin-dashboard-login" replace />;
}

export default App;
